import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { DropdownItem, DropdownMenu, ExportIconImg, IconImg } from './DropDownExportObservations.styled'

import ExcelIcon from '@assets/images/excel-icon.svg'
import PdfIcon from '@assets/images/pdf-icon.svg'
import UploadIcon from '@assets/images/upload-icon.svg'

import { ExportType } from '@utils/hooks'

import { generateExcelFile } from '@libs/excel-js/utils'

import { useLazyProjectWithObservations } from '@hooks/project/project-with-observation.hook'

import { Button } from '@components/Button'
import { DropDown } from '@components/DropDown'

import { ObservationReportInput, ObservationStatus } from '@types'

import { PopUpExportObservations } from '../PopUpExportObservations'

import { useSendReportObservations } from '@hooks/report/send-report-observations.hook'

type Props = {
  exportOptions?: { id: ExportType; label: string; icon: string }[]
  projectId: string
}

export const DropDownExportObservations: FC<Props> = ({
  exportOptions = [
    { id: ExportType.PDF, label: 'PDF', icon: PdfIcon },
    { id: ExportType.EXCEL, label: 'Excel', icon: ExcelIcon },
  ],
  projectId,
}) => {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { getData } = useLazyProjectWithObservations(projectId)
  const { sendObservationsReport } = useSendReportObservations()

  const [showExportPopUp, setShowExportPopUp] = useState<boolean>(false)

  const handleExport = async (type: ExportType) => {
    if (type === ExportType.PDF) {
      setShowExportPopUp(true)
      return
    }

    if (type === ExportType.EXCEL) {
      const projectData = await getData(projectId)
      if (!projectData || !projectData.data) return

      const { project } = projectData.data
      const fileName = project?.name ?? 'observations'

      const headers = [
        t('modules_project-observations_pages_observations_table_label_id'),
        t('modules_project-observations_pages_observations_table_label_name'),
        t('modules_project-observations_pages_observations_table_label_trade'),
        t('modules_project-observations_pages_observations_table_label_company'),
        t('modules_project-observations_pages_observations_table_label_plan'),
        t('modules_project-observations_pages_observations_table_label_phase'),
        t('modules_project-observations_pages_observations_table_label_status'),
        t('modules_project-observations_pages_observations_table_label_created_date'),
        t('modules_project-observations_pages_observations_table_label_limit_date'),
        t('modules_project-observations_pages_observations_table_label_validated_by_reader_date'),
        t('modules_project-observations_pages_observations_table_label_validated_by_admin_date'),
        t('modules_project-observations_pages_observations_table_label_validated_by_customer'),
      ]

      const getStatusTranslationKey = (status: ObservationStatus): string => {
        switch (status) {
          case ObservationStatus.StatusToDo:
            return t('enum_ObservationStatus_to_do_label')
          case ObservationStatus.StatusContested:
            return t('enum_ObservationStatus_contested')
          case ObservationStatus.StatusPlannedRecovery:
            return t('enum_ObservationStatus_planned_recovery')
          case ObservationStatus.StatusValidatedByReader:
            return t('enum_ObservationStatus_validated_by_reader')
          case ObservationStatus.StatusValidatedByCustomer:
            return t('enum_ObservationStatus_validated_by_customer')
          case ObservationStatus.StatusValidatedByAdmin:
            return t('enum_ObservationStatus_validated_by_admin')
          default:
            return ''
        }
      }

      generateExcelFile(
        headers,
        project.observations,
        fileName,
        getStatusTranslationKey,
        Object.fromEntries(searchParams.entries()),
      )
    }
  }

  return (
    <>
      <DropDown
        WrappedComponent={() => (
          <Button>
            <ExportIconImg src={UploadIcon} />
            {t('modules_project-settings_components_Planning_export')}
          </Button>
        )}
        WrappedChildren={({ hide }) => (
          <DropdownMenu
            onMouseMove={(event) => {
              event.stopPropagation()
            }}
          >
            {exportOptions.map(({ id, label, icon }) => (
              <DropdownItem
                key={id}
                onClick={() => {
                  handleExport(id)
                  hide()
                }}
              >
                <IconImg src={icon} alt={label} />
                {label}
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      />

      {showExportPopUp && (
        <PopUpExportObservations
          onSubmit={(options) => {
            const filters = Object.fromEntries(searchParams.entries())

            const input: ObservationReportInput = {
              ...filters,
              ...options,
              projectId,
            }

            sendObservationsReport(input)

            setShowExportPopUp(false)
          }}
          onClose={() => {
            setShowExportPopUp(false)
          }}
        />
      )}
    </>
  )
}
